import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { TaskListSkeleton } from '../../components/TaskListSkeleton/TaskListSkeleton';
import { PaidRelanceItem } from '../../features/relance/PaidRelanceItem';
import { RelanceAgenda } from '../../features/relance/RelanceAgenda';
import { Banner } from '../../ui/Banner/Banner';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { TranslationKey } from '../../i18n/translationKeys';
import { RelanceTab, useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { HomeAgendaSkeleton } from './HomeAgendaSkeleton';
import { RelanceSection } from './RelanceSection';
import { HomeUpcomingRelances } from './HomeUpcomingRelances';
import { IgnoredRelancesSummary } from './IgnoredRelancesSummary';
import type { HomeRelanceSectionsProps } from './types';

export const HomeRelanceSections: React.FC<HomeRelanceSectionsProps> = ({
    entries, aRelancerCount, aRelancerLoading, aRelancerError, onRetryARelancer, waitingOnGreeting,
    canCollectPayment, paidEnabled,
}) => {
    const { t } = useTranslation();
    const {
        deactivatedLoading, deactivatedLoaded, error: deactivationError,
        paidItems, paidLoading, paidError, fetchPaidItems,
        paidTotalCount, paidLoadingMore, fetchMorePaidItems,
    } = useRelanceDeactivationStore(useShallow(s => ({
        deactivatedLoading: s.deactivatedLoading, deactivatedLoaded: s.deactivatedLoaded, error: s.error,
        paidItems: s.paidItems, paidLoading: s.paidLoading, paidError: s.paidError, fetchPaidItems: s.fetchPaidItems,
        paidTotalCount: s.paidTotalCount, paidLoadingMore: s.paidLoadingMore, fetchMorePaidItems: s.fetchMorePaidItems,
    })));

    return (
        <div className="flex flex-col gap-3xl">
            <RelanceSection
                id={RelanceTab.ARelancer}
                title={t(TranslationKey.Home.Tabs.ARelancer)}
                titleHidden
                count={aRelancerCount}
                loading={waitingOnGreeting || aRelancerLoading || (deactivatedLoading && !deactivatedLoaded)}
                error={aRelancerError}
                errorTestId="home-task-error"
                errorAction={
                    <LinkButton size="xs" data-testid="home-task-error-retry" onClick={onRetryARelancer} className="w-fit">
                        {t(TranslationKey.Home.Agenda.Retry)}
                    </LinkButton>
                }
                skeleton={<HomeAgendaSkeleton />}
            >
                <div className="flex flex-col gap-2xl">
                    {entries.today.length === 0 ? (
                        <Banner tone="success" testId="home-empty-arelancer">
                            <span className="font-semibold">{t(TranslationKey.Home.EmptyARelancer)}</span>
                        </Banner>
                    ) : (
                        <section aria-labelledby="home-today-heading" data-testid="home-section-today" className="flex flex-col gap-m">
                            <h3 id="home-today-heading" className="text-block-title-emphasized text-content">
                                {t(TranslationKey.Home.Agenda.TodayTitle, { count: entries.today.length })}
                            </h3>
                            <RelanceAgenda entries={entries.today} canCollectPayment={canCollectPayment} />
                        </section>
                    )}
                    <HomeUpcomingRelances upcoming={entries.upcoming} canCollectPayment={canCollectPayment} />
                    <div className="flex flex-col gap-m empty:hidden">
                        {deactivationError && (
                            <p data-testid="home-section-error-repoussees" className="text-s text-danger">{t(TranslationKey.ApiErrors.Generic)}</p>
                        )}
                        <IgnoredRelancesSummary ignored={entries.ignored} />
                    </div>
                </div>
            </RelanceSection>

            {paidEnabled && (
                <RelanceSection
                    id={RelanceTab.Paid}
                    skeleton={<TaskListSkeleton />}
                    title={t(TranslationKey.Home.Tabs.Encaissees)}
                    count={paidTotalCount}
                    loading={waitingOnGreeting || paidLoading}
                    error={paidError}
                    errorAction={
                        <LinkButton size="xs" data-testid="home-paid-retry" onClick={() => void fetchPaidItems()} className="w-fit">
                            {t(TranslationKey.Home.Paid.Retry)}
                        </LinkButton>
                    }
                >
                    {paidItems.length === 0 ? (
                        <p className="text-s text-primary-700" data-testid="home-empty-encaissees">
                            {t(TranslationKey.Home.Deactivated.Empty.Encaissees)}
                        </p>
                    ) : (
                        <div className="flex flex-col gap-s">
                            <ul role="list" className="flex flex-col gap-s">
                                {paidItems.map(item => <PaidRelanceItem key={item.id} item={item} />)}
                            </ul>
                            <p className="text-xs text-primary-700" data-testid="home-paid-count-hint">
                                {t(TranslationKey.Home.Paid.CountHint, { loaded: paidItems.length, total: paidTotalCount })}
                            </p>
                            {paidItems.length < paidTotalCount && (
                                <LinkButton
                                    size="xs"
                                    data-testid="home-paid-load-more"
                                    disabled={paidLoadingMore}
                                    onClick={() => void fetchMorePaidItems()}
                                    className="w-fit"
                                >
                                    {t(TranslationKey.Home.Paid.LoadMore)}
                                </LinkButton>
                            )}
                        </div>
                    )}
                </RelanceSection>
            )}
        </div>
    );
};
